import { Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Briefcase, ExternalLink, MapPin, ShieldAlert, ShieldCheck, ShieldOff, Star } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useState } from "react";

interface ActiveJob {
  id: number;
  title: string;
  practice_area: string | null;
  job_type: string | null;
  locations: string[] | null;
  jd_year_range: string | null;
  last_seen_at: string | null;
  leopard_job_id: string | null;
  status: string;
  sourcing_status: string | null;
}

interface RecentAlert {
  id: number;
  subject: string | null;
  alert_type: string;
  market: string;
  position: string | null;
  practice_area: string | null;
  location: string | null;
  received_at: string | null;
  processed: boolean;
}

interface FirmCandidate {
  id: number;
  full_name: string;
  practice_area: string;
  location: string;
  jd_year: number | null;
  pipeline_stage: string;
  dnc_status: string;
  submission: { id: number; job_title: string } | null;
}

interface DncRecord {
  id: number;
  scope: string;
  scope_type: string | null;
  placement_office: string | null;
  practice_area: string | null;
  is_ongoing: boolean;
  end_date_parsed: string | null;
  expired: boolean;
  forfeiture: boolean | null;
  notes: string | null;
}

interface FirmData {
  id: number;
  name: string;
  url: string | null;
  tier: number | null;
  short_name: string | null;
  locations: string[];
  relationship_notes: string | null;
  relationship_rating: number | null;
  placement_count: number;
  active_job_count: number;
  last_interaction_at: string | null;
  contact_preferences: string | null;
}

interface Props {
  firm: FirmData;
  candidates: FirmCandidate[];
  dnc_records: DncRecord[];
  active_jobs: ActiveJob[];
  recent_alerts: RecentAlert[];
}

function relativeDate(iso: string | null): string {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 56) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const SOURCING_CLASSES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  sourced: "bg-green-100 text-green-700",
  skipped: "bg-gray-100 text-gray-500",
};

const ALERT_DOT: Record<string, string> = {
  new_job:     "bg-green-500",
  closed_job:  "bg-red-500",
  changed_job: "bg-amber-400",
};

function ActiveJobsPanel({ jobs }: { jobs: ActiveJob[] }) {
  return (
    <div className="mb-6">
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Active Jobs</h2>
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {jobs.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">No active jobs at this firm.</p>
        ) : (
          jobs.map((j) => (
            <div key={j.id} className="px-4 py-3 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link href={`/jobs/${j.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate">
                    {j.title}
                  </Link>
                  {j.sourcing_status && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${SOURCING_CLASSES[j.sourcing_status] ?? "bg-gray-100 text-gray-500"}`}>
                      {j.sourcing_status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                  {j.practice_area && <span>{j.practice_area}</span>}
                  {j.locations && j.locations.length > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />
                      {j.locations[0]}{j.locations.length > 1 && ` +${j.locations.length - 1}`}
                    </span>
                  )}
                  {j.jd_year_range && <span>JD {j.jd_year_range}</span>}
                </div>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                {relativeDate(j.last_seen_at)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function RecentAlertsPanel({ alerts }: { alerts: RecentAlert[] }) {
  return (
    <div className="mb-6">
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Recent Alerts</h2>
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {alerts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">No recent alerts for this firm.</p>
        ) : (
          alerts.map((a) => (
            <div key={a.id} className="px-4 py-3 flex items-center gap-3">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ALERT_DOT[a.alert_type] ?? "bg-gray-300"}`} />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-gray-800 truncate block">{a.subject ?? a.alert_type}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-xs">{a.market}</Badge>
                  {a.practice_area && <span className="text-xs text-gray-400">{a.practice_area}</span>}
                </div>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                {relativeDate(a.received_at)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead",
  intake_scheduled: "Intake Scheduled",
  resume_editing: "Resume Editing",
  package_pending: "Package Pending",
  submitted: "Submitted",
  screener: "Screener",
  full_round: "Full Round",
  offer_placed: "Offer / Placed",
};

function DncIcon({ status }: { status: string }) {
  if (status === "clear") return <ShieldCheck className="w-4 h-4 text-green-500" />;
  if (status === "blocked_both") return <ShieldOff className="w-4 h-4 text-red-700" />;
  return <ShieldAlert className="w-4 h-4 text-red-500" />;
}

function RatingStars({ rating, onChange }: { rating: number | null; onChange: (r: number) => void }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="focus:outline-none"
          title={`Rate ${n}`}
        >
          <Star
            className={`w-4 h-4 ${n <= (rating ?? 0) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function FirmShow({ firm, candidates, dnc_records, active_jobs, recent_alerts }: Props) {
  const activeDnc = dnc_records.filter((r) => !r.expired);
  const [editingNotes, setEditingNotes] = useState(false);

  const { data, setData, patch, processing } = useForm({
    relationship_notes:  firm.relationship_notes ?? "",
    relationship_rating: firm.relationship_rating ?? null as number | null,
  });

  function saveRelationship() {
    patch(`/firms/${firm.id}/update_relationship`, {
      onSuccess: () => setEditingNotes(false),
    });
  }

  return (
    <AppLayout title={firm.name}>
      <Link href="/firms" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Firms
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">{firm.name}</h1>
            {firm.short_name && (
              <span className="text-sm text-gray-400">({firm.short_name})</span>
            )}
            {activeDnc.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                <ShieldAlert className="w-3 h-3" /> {activeDnc.length} active DNC
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {firm.url && (
              <a href={firm.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-blue-600">
                <ExternalLink className="w-3 h-3" /> Leopard profile
              </a>
            )}
            {firm.locations.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {firm.locations.slice(0, 3).join(", ")}
                {firm.locations.length > 3 && ` +${firm.locations.length - 3} more`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600 text-right">
          <div className="text-center">
            <div className="text-xl font-semibold text-gray-900">{candidates.length}</div>
            <div className="text-xs text-gray-400">attorneys</div>
          </div>
          {firm.placement_count > 0 && (
            <div className="text-center">
              <div className="text-xl font-semibold text-green-600">{firm.placement_count}</div>
              <div className="text-xs text-gray-400">placed</div>
            </div>
          )}
          {firm.active_job_count > 0 && (
            <div className="text-center">
              <div className="text-xl font-semibold text-blue-600 flex items-center gap-1 justify-center">
                <Briefcase className="w-4 h-4" />{firm.active_job_count}
              </div>
              <div className="text-xs text-gray-400">open jobs</div>
            </div>
          )}
        </div>
      </div>

      {/* Relationship */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Relationship</h2>
          {!editingNotes && (
            <Button variant="outline" size="sm" onClick={() => setEditingNotes(true)}>
              Edit
            </Button>
          )}
        </div>
        {editingNotes ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Rating</label>
              <RatingStars
                rating={data.relationship_rating}
                onChange={(r) => setData("relationship_rating", r)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Notes</label>
              <textarea
                className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                rows={3}
                value={data.relationship_notes}
                onChange={(e) => setData("relationship_notes", e.target.value)}
                placeholder="Notes about this firm relationship..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveRelationship}
                disabled={processing}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setEditingNotes(false)}
                className="text-xs text-gray-500 px-3 py-1.5 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {data.relationship_rating ? (
              <RatingStars rating={data.relationship_rating} onChange={() => {}} />
            ) : null}
            {data.relationship_notes ? (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.relationship_notes}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">No notes yet.</p>
            )}
            {firm.last_interaction_at && (
              <p className="text-xs text-gray-400">
                Last activity: {new Date(firm.last_interaction_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* DNC Records */}
      {dnc_records.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">DNC Records</h2>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {dnc_records.map((r) => (
              <div key={r.id} className={`px-4 py-3 flex items-start gap-3 ${r.expired ? "opacity-50" : ""}`}>
                <ShieldAlert className={`w-4 h-4 mt-0.5 flex-shrink-0 ${r.expired ? "text-gray-300" : "text-red-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{r.scope}</span>
                    {r.is_ongoing && (
                      <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Ongoing</span>
                    )}
                    {r.forfeiture && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Forfeiture</span>
                    )}
                    {r.expired && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Expired</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 space-x-2">
                    {r.placement_office && <span>{r.placement_office}</span>}
                    {r.practice_area && <span>{r.practice_area}</span>}
                    {r.end_date_parsed && (
                      <span>Until {new Date(r.end_date_parsed).toLocaleDateString()}</span>
                    )}
                  </div>
                  {r.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{r.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ActiveJobsPanel jobs={active_jobs} />
      <RecentAlertsPanel alerts={recent_alerts} />

      {/* Candidates */}
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Attorneys</h2>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Practice</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">JD</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">DNC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {candidates.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">No candidates at this firm.</td>
              </tr>
            )}
            {candidates.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link href={`/candidates/${c.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600">
                    {c.full_name}
                  </Link>
                  {c.submission && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      → submitted to{" "}
                      <Link href={`/submissions/${c.submission.id}`} className="text-blue-600 hover:text-blue-700">
                        {c.submission.job_title}
                      </Link>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{c.practice_area}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{c.location}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{c.jd_year ?? "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="text-xs">
                    {STAGE_LABELS[c.pipeline_stage] ?? c.pipeline_stage}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <DncIcon status={c.dnc_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
